import type { ReactElement, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Reusable error surface shared by page-level and section-level error
 * states. Centralises the destructive-border card, warning icon, heading
 * + message + optional error-code block, and an ``actions`` slot for
 * retry / navigation buttons.
 *
 * Kept presentational on purpose — it does not know about React Query,
 * ``ApiError``, or i18n. Callers compose the localised copy and decide
 * which ``code`` to expose (``ApiError.code``, HTTP status, etc.).
 */
export interface ErrorCalloutProps {
  readonly heading: string;
  readonly message: string;
  /** Optional machine-readable error code (e.g. ``ApiError.code``). */
  readonly code?: string;
  /** Label rendered before the code (e.g. ``"Error code"``). */
  readonly codeLabel?: string;
  /** Retry / navigation buttons rendered below the body copy. */
  readonly actions?: ReactNode;
  /** Accessibility role — defaults to ``alert`` for page-level failures. */
  readonly role?: 'alert' | 'status';
  /**
   * Element used to render the heading. Page-level callouts should use
   * a real heading (``'h2'`` / ``'h3'``) so screen-reader heading
   * navigation reaches the error. Section-level callouts living inside
   * a `Card` with its own `CardTitle` should default to ``'p'`` to
   * avoid duplicating heading levels.
   */
  readonly headingAs?: 'p' | 'h2' | 'h3' | 'h4';
  readonly className?: string;
}

export function ErrorCallout({
  heading,
  message,
  code,
  codeLabel,
  actions,
  role = 'alert',
  headingAs: HeadingTag = 'p',
  className,
}: ErrorCalloutProps): ReactElement {
  return (
    <div
      role={role}
      className={cn(
        'flex items-start gap-3 rounded-lg border border-destructive/50 bg-destructive/10 p-4',
        className,
      )}
    >
      <AlertTriangle
        className="h-5 w-5 text-destructive shrink-0 mt-0.5"
        aria-hidden="true"
      />
      <div className="flex-1">
        <HeadingTag className="text-sm font-semibold text-card-foreground">
          {heading}
        </HeadingTag>
        <p className="mt-1 text-sm text-muted-foreground">{message}</p>
        {code !== undefined && (
          <p className="mt-2 text-xs text-muted-foreground font-mono">
            {codeLabel !== undefined ? `${codeLabel}: ${code}` : code}
          </p>
        )}
        {actions !== undefined && (
          <div className="mt-3 flex flex-wrap gap-2">{actions}</div>
        )}
      </div>
    </div>
  );
}
